import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { CURRENCIES, currencyCode, setCurrency } from '../utils/currency';

const CSV_TABLES = [
  { key: 'sheep', label: 'Sheep' },
  { key: 'vaccinations', label: 'Vaccinations' },
  { key: 'treatments', label: 'Treatments' },
  { key: 'health_events', label: 'Health Events' },
  { key: 'weights', label: 'Weights' },
  { key: 'lambing_records', label: 'Lambing' },
  { key: 'breeding_records', label: 'Breeding' },
  { key: 'farm_transactions', label: 'Transactions' },
];

export default function Settings({ user, onLogout }) {
  const [backups, setBackups] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [currency, setCurrencyState] = useState(currencyCode());

  const changeCurrency = (code) => {
    setCurrency(code);
    setCurrencyState(code);
    setMessage(`Currency set to ${code}.`);
  };

  const loadBackups = () => {
    api.listBackups().then(setBackups).catch(err => setError(err.message));
  };

  useEffect(() => { loadBackups(); }, []);

  const createBackup = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const backup = await api.createBackup();
      setMessage(`Backup created: ${backup.filename}`);
      loadBackups();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const exportData = () => {
    window.location.href = api.exportUrl();
  };

  const logout = async () => {
    await api.logout();
    onLogout();
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">{user?.username || 'Admin'}</p>
        </div>
        <button className="btn btn-secondary" onClick={logout}>Sign Out</button>
      </div>

      {(message || error) && (
        <div className={`alert ${error ? 'alert-danger' : 'alert-success'} mb-4`}>
          {error || message}
        </div>
      )}

      <div className="settings-grid">
        <section className="card">
          <div className="section-header">
            <span className="section-title">Backups</span>
            <button className="btn btn-primary btn-sm" onClick={createBackup} disabled={busy}>
              {busy ? 'Creating...' : 'Create Backup'}
            </button>
          </div>

          {backups.length === 0 ? (
            <p className="text-muted">No backups yet.</p>
          ) : (
            <div className="backup-list">
              {backups.slice(0, 8).map(backup => (
                <div className="backup-row" key={backup.filename}>
                  <strong>{backup.filename}</strong>
                  <span>{Math.round(backup.size_bytes / 1024)} KB</span>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="card">
          <div className="section-header">
            <span className="section-title">Export</span>
            <button className="btn btn-secondary btn-sm" onClick={exportData}>Download JSON</button>
          </div>
          <p className="text-muted">Full backup of all records as JSON. Download individual tables as CSV below.</p>
          <div className="csv-grid mt-3">
            {CSV_TABLES.map(t => (
              <a key={t.key} className="btn btn-ghost btn-sm" href={api.exportTableUrl(t.key)}>{t.label} CSV</a>
            ))}
          </div>
        </section>

        <section className="card">
          <div className="section-header">
            <span className="section-title">Currency</span>
          </div>
          <p className="text-muted">Used across all monetary displays and forms.</p>
          <select
            className="filter-select mt-2"
            value={currency}
            onChange={e => changeCurrency(e.target.value)}
            aria-label="Currency"
          >
            {Object.values(CURRENCIES).map(c => (
              <option key={c.code} value={c.code}>{c.code} — {c.label}</option>
            ))}
          </select>
        </section>
      </div>
    </div>
  );
}
